/**
 * Brojanica PDP orbital viewer — vanilla Three.js (CDN ESM)
 * Single GLB with KHR_materials_variants + KHR_draco_mesh_compression.
 * Boot lazily via BrojanicaProductViewer.bootFromMount when 3D tab opens.
 */
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.js';
import { OrbitControls } from 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/loaders/GLTFLoader.js';
import { RGBELoader } from 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/loaders/RGBELoader.js';
import { DRACOLoader } from 'https://cdn.jsdelivr.net/npm/three@0.170.0/examples/jsm/loaders/DRACOLoader.js';

/** Soft warm studio canvas — brand cream; HDRI for metal reflections only */
const BG = 0x1c1a17;

/** Cord id → KHR_materials_variants name (exact strings in BRJ-33-14K-variants.glb) */
const CORD_TO_VARIANT = {
  red: 'Red',
  blue: 'Cobalt blue',
  black: 'Black',
};

function cordIdFromLabel(label) {
  const s = String(label || 'red').toLowerCase();
  if (s.includes('blue') || s.includes('cobalt')) return 'blue';
  if (s.includes('black')) return 'black';
  if (s.includes('red')) return 'red';
  return 'red';
}

function variantNameForCord(cordId) {
  return CORD_TO_VARIANT[cordId] || CORD_TO_VARIANT.red;
}

function centerObject(root) {
  const box = new THREE.Box3().setFromObject(root);
  const center = box.getCenter(new THREE.Vector3());
  root.position.sub(center);
}

/** Same on-screen size for every cord (black was exporting larger). */
function fitObject(root, targetSize = 0.055) {
  centerObject(root);
  const box = new THREE.Box3().setFromObject(root);
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z) || 1;
  root.scale.multiplyScalar(targetSize / maxDim);
  centerObject(root);
}

function enableShadows(root) {
  root.traverse((obj) => {
    if (!obj.isMesh) return;
    obj.castShadow = true;
    obj.receiveShadow = true;
  });
}

export class ProductViewer {
  constructor(mount, options = {}) {
    this.mount = mount;
    this.modelUrl = options.modelUrl || '';
    this.models = options.models || {};
    this.fallbackUrl = options.fallbackUrl || this.models.red || '';
    this.hdriUrl = options.hdriUrl || '';
    this.cordId = cordIdFromLabel(options.cordColor || 'red');
    this._autoRotate = true;
    this._destroyed = false;
    this._modelRoot = null;
    this._gltfParser = null;
    this._variantsExt = null;
    this._loading = false;
    this._raf = 0;
    this._resizeObs = null;
    this._loadedOnce = false;
    this._activeVariant = null;
    this._variantSwitchSeq = 0;

    this._build();
    this.loadCord(this.cordId);
    this._start();
  }

  /**
   * CSS pixel size of the square gallery frame.
   * The frame is width:100% and height:0 + padding-bottom:100%, so the
   * used box is a square derived from width. Reading height separately
   * can come back 0 or a different number (the black swatch is the
   * default, so that first measure is the one that looked wrong).
   * Do not hardcode 512: on a desktop 1080p window this column is ~516px,
   * and below 1024px it is the full content width.
   */
  _cssSquare() {
    const frame = this.mount.closest('.product__frame, .product-frame') || this.mount;
    const rect = frame.getBoundingClientRect();
    const width = Math.floor(rect.width);
    if (width > 0) return width;
    return Math.max(Math.floor(rect.height), this.mount.clientWidth, this.mount.clientHeight, 1);
  }

  _applyBufferSize() {
    if (this._destroyed || !this.renderer || !this.camera) return;
    const css = this._cssSquare();
    if (css === this._cssSize) return;
    this._cssSize = css;
    this.camera.aspect = 1;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
    this.renderer.setSize(css, css, false);
    const canvas = this.renderer.domElement;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
  }

  _build() {
    const css = 320;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(BG);

    this.camera = new THREE.PerspectiveCamera(32, 1, 0.01, 5);
    this.camera.position.set(0.08, 0.045, 0.1);

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
    this.renderer.setSize(css, css, false);
    this.renderer.setClearColor(BG, 1);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.domElement.style.position = 'absolute';
    this.renderer.domElement.style.inset = '0';
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.renderer.domElement.style.display = 'block';
    this.renderer.domElement.style.maxWidth = '100%';
    this.renderer.domElement.style.maxHeight = '100%';
    this.renderer.domElement.style.touchAction = 'none';
    this.mount.appendChild(this.renderer.domElement);

    // Studio fill while HDRI loads — Poly Haven White Studio 03 (neutral)
    this.scene.add(new THREE.AmbientLight(0xfff6ea, 0.42));
    const key = new THREE.DirectionalLight(0xffffff, 1.15);
    key.position.set(0.55, 0.9, 0.55);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xffe8c8, 0.55);
    fill.position.set(-0.65, 0.45, -0.25);
    this.scene.add(fill);
    const rim = new THREE.DirectionalLight(0xffffff, 0.35);
    rim.position.set(-0.1, 0.2, -0.75);
    this.scene.add(rim);
    const bounce = new THREE.DirectionalLight(0xc9a227, 0.18);
    bounce.position.set(0.2, -0.55, 0.3);
    this.scene.add(bounce);

    this._loadStudioHdri();

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.06;
    this.controls.enablePan = false;
    this.controls.autoRotate = true;
    this.controls.autoRotateSpeed = 0.55;
    this.controls.minPolarAngle = Math.PI * 0.28;
    this.controls.maxPolarAngle = Math.PI * 0.72;
    this.controls.minDistance = 0.055;
    this.controls.maxDistance = 0.2;
    this.controls.target.set(0, 0, 0);
    this.controls.addEventListener('start', () => {
      this._autoRotate = false;
      this.controls.autoRotate = false;
    });

    this._dracoLoader = new DRACOLoader();
    this._dracoLoader.setDecoderPath(
      'https://www.gstatic.com/draco/versioned/decoders/1.5.7/',
    );
    this.loader = new GLTFLoader();
    this.loader.setDRACOLoader(this._dracoLoader);

    this._group = new THREE.Group();
    this.scene.add(this._group);

    this._onResize = () => this.resize();
    window.addEventListener('resize', this._onResize);
    const frame = this.mount.closest('.product__frame, .product-frame') || this.mount;
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObs = new ResizeObserver(() => this.resize());
      this._resizeObs.observe(frame);
    }
    this._applyBufferSize();
  }

  _loadStudioHdri() {
    if (!this.hdriUrl) return;
    try {
      const loader = new RGBELoader();
      loader.load(
        this.hdriUrl,
        (texture) => {
          if (this._destroyed) {
            texture.dispose();
            return;
          }
          try {
            const pmrem = new THREE.PMREMGenerator(this.renderer);
            pmrem.compileEquirectangularShader();
            const env = pmrem.fromEquirectangular(texture).texture;
            this.scene.environment = env;
            this.scene.environmentIntensity = .8;
            // Keep solid studio BG — HDRI only for metal reflections
            this.renderer.toneMappingExposure = 1.5;
            texture.dispose();
            pmrem.dispose();
          } catch (e) {
            console.warn('[Brojanica viewer] HDRI process failed', e);
            texture.dispose();
          }
        },
        undefined,
        (err) => console.warn('[Brojanica viewer] HDRI load failed', err),
      );
    } catch (e) {
      console.warn('[Brojanica viewer] HDRI setup failed', e);
    }
  }

  resize() {
    this._applyBufferSize();
  }

  _setLoading(on) {
    this._loading = on;
    this.mount.classList.toggle('is-loading', !!on);
    const el = this.mount.querySelector('[data-viewer-status]');
    if (el) el.hidden = !on;
  }

  /**
   * Apply KHR_materials_variants mapping by exact variant name.
   * Uses gltf.parser.getDependency('material', index) per Three.js example.
   */
  async selectVariant(variantName) {
    if (!this._modelRoot || !this._gltfParser || !this._variantsExt) return;
    const variants = this._variantsExt.variants || [];
    const variantIndex = variants.findIndex((v) => v.name === variantName);
    if (variantIndex < 0) {
      console.warn('[Brojanica viewer] Unknown variant', variantName, 'known:', variants.map((v) => v.name));
      return;
    }

    const seq = ++this._variantSwitchSeq;
    const parser = this._gltfParser;
    const root = this._modelRoot;
    const pending = [];

    root.traverse((object) => {
      if (!object.isMesh || !object.userData || !object.userData.gltfExtensions) return;
      const meshVariantDef = object.userData.gltfExtensions['KHR_materials_variants'];
      if (!meshVariantDef || !meshVariantDef.mappings) return;

      if (!object.userData.originalMaterial) {
        object.userData.originalMaterial = object.material;
      }

      const mapping = meshVariantDef.mappings.find(
        (m) => Array.isArray(m.variants) && m.variants.includes(variantIndex),
      );

      if (mapping) {
        pending.push(
          parser.getDependency('material', mapping.material).then((material) => {
            if (this._destroyed || seq !== this._variantSwitchSeq) return;
            object.material = material;
            parser.assignFinalMaterial(object);
          }),
        );
      } else if (object.userData.originalMaterial) {
        object.material = object.userData.originalMaterial;
      }
    });

    await Promise.all(pending);
    if (!this._destroyed && seq === this._variantSwitchSeq) {
      this._activeVariant = variantName;
    }
  }

  async _loadModelOnce(url) {
    const gltf = await new Promise((resolve, reject) => {
      this.loader.load(url, resolve, undefined, reject);
    });
    if (this._destroyed) return;

    while (this._group.children.length) {
      const c = this._group.children[0];
      this._group.remove(c);
      c.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        // Do not dispose materials — shared via parser cache / variants
      });
    }

    const root = gltf.scene;
    fitObject(root, 0.055);
    enableShadows(root);
    this._group.add(root);
    this._modelRoot = root;
    this._gltfParser = gltf.parser;
    this._variantsExt =
      (gltf.userData && gltf.userData.gltfExtensions && gltf.userData.gltfExtensions['KHR_materials_variants']) ||
      null;
    this._loadedOnce = true;
    this._autoRotate = true;
    this.controls.autoRotate = true;
    this.resize();
  }

  /**
   * Switch cord colour. First call loads the variants GLB once;
   * later calls only switch KHR_materials_variants (no reload).
   */
  async loadCord(cordLabelOrId) {
    if (this._destroyed) return;
    const id = cordIdFromLabel(cordLabelOrId);
    this.cordId = id;
    const variantName = variantNameForCord(id);

    if (this._loadedOnce && this._modelRoot) {
      if (this._activeVariant === variantName) return;
      await this.selectVariant(variantName);
      return;
    }

    const url =
      this.modelUrl ||
      this.models[id] ||
      this.fallbackUrl;
    if (!url) return;

    this._setLoading(true);
    try {
      await this._loadModelOnce(url);
      if (this._destroyed) return;
      await this.selectVariant(variantName);
    } catch (err) {
      console.warn('[Brojanica viewer] GLB load failed', url, err);
      // Per-color fallback only when modelUrl missing or failed
      if (this.modelUrl && this.models[id] && this.models[id] !== url) {
        this.modelUrl = '';
        this._loadedOnce = false;
        await this.loadCord(id);
        return;
      }
      if (!this.modelUrl && url !== this.fallbackUrl && this.fallbackUrl) {
        this.models[id] = null;
        this._loadedOnce = false;
        await this.loadCord(id);
        return;
      }
    } finally {
      if (!this._destroyed) this._setLoading(false);
    }
  }

  show() {
    this.mount.hidden = false;
    this.resize();
    if (!this._raf) this._start();
  }

  hide() {
    this.mount.hidden = true;
  }

  _start() {
    const tick = () => {
      if (this._destroyed) return;
      this._raf = requestAnimationFrame(tick);
      if (this.mount.hidden) return;
      this.controls.autoRotate = this._autoRotate;
      this.controls.update();
      this.renderer.render(this.scene, this.camera);
    };
    this._raf = requestAnimationFrame(tick);
  }

  destroy() {
    this._destroyed = true;
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = 0;
    window.removeEventListener('resize', this._onResize);
    if (this._resizeObs) this._resizeObs.disconnect();
    if (this.controls) this.controls.dispose();
    if (this._dracoLoader) this._dracoLoader.dispose();
    if (this.renderer) {
      this.renderer.dispose();
      if (this.renderer.domElement && this.renderer.domElement.parentNode) {
        this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
      }
    }
    if (this.scene) {
      this.scene.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) {
          const mats = Array.isArray(o.material) ? o.material : [o.material];
          mats.forEach((m) => m && m.dispose && m.dispose());
        }
      });
    }
  }
}

function bootFromMount(mount) {
  if (!mount) return null;
  if (mount._brojanicaViewer) return mount._brojanicaViewer;
  mount.dataset.viewerReady = '1';
  const modelUrl = mount.dataset.modelUrl || '';
  const models = {
    red: mount.dataset.modelRed || '',
    blue: mount.dataset.modelBlue || '',
    black: mount.dataset.modelBlack || '',
  };
  const fallbackUrl = mount.dataset.modelFallback || models.red || '';
  const cordColor = mount.dataset.cordColor || 'red';
  const hdriUrl = mount.dataset.hdriUrl || '';
  const viewer = new ProductViewer(mount, {
    modelUrl,
    models,
    fallbackUrl,
    cordColor,
    hdriUrl,
  });
  mount._brojanicaViewer = viewer;
  return viewer;
}

export function getViewer(root) {
  const mount = (root || document).querySelector('[data-product-viewer]');
  if (!mount) return null;
  return mount._brojanicaViewer || bootFromMount(mount);
}

export function destroyViewer(root) {
  const mount = (root || document).querySelector('[data-product-viewer]');
  if (mount && mount._brojanicaViewer) {
    mount._brojanicaViewer.destroy();
    mount._brojanicaViewer = null;
    delete mount.dataset.viewerReady;
  }
}

// No auto-boot — theme.js ensureViewer boots on 3D tab open (saves mobile data)

document.addEventListener('shopify:section:unload', (e) => {
  if (e.target && e.target.querySelector && e.target.querySelector('[data-product-viewer]')) {
    destroyViewer(e.target);
  }
});

window.BrojanicaProductViewer = { ProductViewer, getViewer, destroyViewer, bootFromMount };
